/**
 * useDialog.js
 *
 * Replaces window.alert / window.confirm / window.prompt with in-app UI:
 *   - showAlert(msg, type)        → styled toast (auto-dismisses, no blocking)
 *   - showConfirm(msg, opts)      → blocking modal → Promise<boolean>
 *   - showPrompt(msg, defaultVal) → blocking modal with input → Promise<string|null>
 *
 * Usage:
 *   const { dialogNode, showAlert, showConfirm, showPrompt } = useDialog();
 *   // render {dialogNode} anywhere in JSX
 *
 *   await showAlert('Saved!');
 *   const ok = await showConfirm('Delete this entry?');
 *   const val = await showPrompt('Enter value:', '');
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';

// ─── Styles ──────────────────────────────────────────────────────────────────

const TOAST_COLORS = {
  success: { bg: '#e8f5e9', border: '#4caf50', color: '#1b5e20', icon: '✅' },
  error:   { bg: '#fdecea', border: '#e53935', color: '#b71c1c', icon: '❌' },
  info:    { bg: '#e3f2fd', border: '#1e88e5', color: '#0d47a1', icon: 'ℹ️' },
  warning: { bg: '#fff8e1', border: '#fb8c00', color: '#e65100', icon: '⚠️' },
};

const overlayStyle = {
  position: 'fixed', inset: 0,
  background: 'rgba(0,0,0,0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 10001,
};

const cardStyle = {
  background: '#fff',
  borderRadius: 10,
  padding: '24px 28px',
  maxWidth: 400,
  width: '90vw',
  boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
  fontFamily: 'inherit',
};

const msgStyle = {
  fontSize: 15,
  color: '#222',
  lineHeight: 1.5,
  marginBottom: 20,
  whiteSpace: 'pre-wrap',
};

const btnRowStyle = {
  display: 'flex',
  gap: 10,
  justifyContent: 'flex-end',
};

const btnBase = {
  padding: '8px 20px',
  borderRadius: 6,
  border: 'none',
  fontSize: 14,
  cursor: 'pointer',
  fontWeight: 600,
};

const btnOk = { ...btnBase, background: '#1976d2', color: '#fff' };
const btnOkDanger = { ...btnBase, background: '#e53935', color: '#fff' };
const btnCancel = { ...btnBase, background: '#eee', color: '#333' };

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid #ccc',
  fontSize: 14,
  marginBottom: 16,
  boxSizing: 'border-box',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function Toast({ msg, type = 'info', onClose }) {
  const c = TOAST_COLORS[type] || TOAST_COLORS.info;
  useEffect(() => {
    const t = setTimeout(onClose, 3200);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div style={{
      position: 'fixed', top: 24, left: '50%', transform: 'translateX(-50%)',
      zIndex: 9999, minWidth: 260, maxWidth: '90vw',
      background: c.bg, border: `1.5px solid ${c.border}`, color: c.color,
      borderRadius: 8, padding: '12px 18px',
      boxShadow: '0 4px 18px rgba(0,0,0,0.15)',
      display: 'flex', alignItems: 'flex-start', gap: 10,
      fontSize: 14, lineHeight: 1.5,
      animation: 'fadeInDown 0.2s ease',
    }}>
      <span style={{ fontSize: 18, marginTop: 1 }}>{c.icon}</span>
      <span style={{ flex: 1, whiteSpace: 'pre-wrap' }}>{msg}</span>
      <button onClick={onClose} style={{
        background: 'none', border: 'none', cursor: 'pointer',
        color: c.color, fontSize: 16, padding: 0, marginLeft: 6, lineHeight: 1,
      }}>×</button>
    </div>
  );
}

function ConfirmModal({ msg, okLabel = 'Yes', cancelLabel = 'No', danger = false, onOk, onCancel }) {
  return (
    <div style={overlayStyle} onClick={onCancel}>
      <div style={cardStyle} onClick={e => e.stopPropagation()}>
        <p style={msgStyle}>{msg}</p>
        <div style={btnRowStyle}>
          <button style={btnCancel} onClick={onCancel}>{cancelLabel}</button>
          <button style={danger ? btnOkDanger : btnOk} onClick={onOk}>{okLabel}</button>
        </div>
      </div>
    </div>
  );
}

function PromptModal({ msg, defaultVal = '', okLabel = 'OK', cancelLabel = 'Cancel', onOk, onCancel }) {
  const [value, setValue] = useState(defaultVal);
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); inputRef.current?.select(); }, []);
  const submit = () => onOk(value);
  return (
    <div style={overlayStyle} onClick={onCancel}>
      <div style={cardStyle} onClick={e => e.stopPropagation()}>
        <p style={msgStyle}>{msg}</p>
        <input
          ref={inputRef}
          style={inputStyle}
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') onCancel(); }}
        />
        <div style={btnRowStyle}>
          <button style={btnCancel} onClick={onCancel}>{cancelLabel}</button>
          <button style={btnOk} onClick={submit}>{okLabel}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useDialog() {
  const [toast, setToast] = useState(null);       // { msg, type }
  const [modal, setModal] = useState(null);        // { mode, msg, defaultVal, danger, okLabel, cancelLabel }
  const resolveRef = useRef(null);

  const dismissToast = useCallback(() => setToast(null), []);

  /** Show a non-blocking toast. type: 'success'|'error'|'info'|'warning' */
  const showAlert = useCallback((msg, type = 'info') => {
    setToast({ msg: String(msg), type });
  }, []);

  /** Show a blocking confirm modal. Returns Promise<boolean>. */
  const showConfirm = useCallback((msg, opts = {}) => {
    return new Promise(resolve => {
      resolveRef.current = resolve;
      setModal({ mode: 'confirm', msg, ...opts });
    });
  }, []);

  /** Show a blocking prompt modal. Returns Promise<string|null> (null = cancelled). */
  const showPrompt = useCallback((msg, defaultVal = '') => {
    return new Promise(resolve => {
      resolveRef.current = resolve;
      setModal({ mode: 'prompt', msg, defaultVal });
    });
  }, []);

  const handleOk = useCallback((value) => {
    const resolve = resolveRef.current;
    setModal(null);
    if (resolve) resolve(modal?.mode === 'confirm' ? true : value);
  }, [modal]);

  const handleCancel = useCallback(() => {
    const resolve = resolveRef.current;
    setModal(null);
    if (resolve) resolve(modal?.mode === 'confirm' ? false : null);
  }, [modal]);

  const dialogNode = (
    <>
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={dismissToast} />}
      {modal?.mode === 'confirm' && (
        <ConfirmModal
          msg={modal.msg}
          okLabel={modal.okLabel}
          cancelLabel={modal.cancelLabel}
          danger={modal.danger}
          onOk={handleOk}
          onCancel={handleCancel}
        />
      )}
      {modal?.mode === 'prompt' && (
        <PromptModal
          msg={modal.msg}
          defaultVal={modal.defaultVal}
          okLabel={modal.okLabel}
          cancelLabel={modal.cancelLabel}
          onOk={handleOk}
          onCancel={handleCancel}
        />
      )}
    </>
  );

  return { dialogNode, showAlert, showConfirm, showPrompt };
}
